import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { ActionConfig, ACTION_CONFIGS } from '../table-action-configs';
import { CustomActionCellRendererComponent } from '../custom-action-cell-renderer/custom-action-cell-renderer.component';
import {
  TABLE_COLUMN_PRESETS,
  DEFAULT_TABLE_COLUMNS,
} from '../table-columns.config';

@Injectable({
  providedIn: 'root',
})
export class TableColumnService {
  getColumnsFromPreset(
    preset: string,
    customPresets: Record<string, ColDef[]> = {},
  ): ColDef[] {
    if (customPresets[preset]) {
      return customPresets[preset];
    }

    const builtInPresets = TABLE_COLUMN_PRESETS as any;
    return builtInPresets[preset] || DEFAULT_TABLE_COLUMNS;
  }

  getActionsForPreset(preset: string): ActionConfig[] {
    const actionConfigs = ACTION_CONFIGS as any;
    return actionConfigs[preset] || ACTION_CONFIGS.DEFAULT;
  }

  addCheckboxColumn(columns: ColDef[]): ColDef[] {
    const checkboxColumn: ColDef = {
      field: '',
      headerName: '',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      pinned: 'left',
      lockPosition: 'left',
      suppressMovable: true,
      sortable: false,
      filter: false,
      resizable: false,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
    };
    return [checkboxColumn, ...columns];
  }

  addActionColumn(
    columns: ColDef[],
    actions: ActionConfig[],
    onActionClick: Function,
  ): ColDef[] {
    if (!actions || actions.length === 0) {
      return columns;
    }

    const calculatedWidth = this.calculateActionColumnWidth(actions);
    const overrideWidth = Math.max(
      ...actions.map((a: any) => (typeof a.columnWidth === 'number' ? a.columnWidth : 0)),
    );
    const finalWidth = overrideWidth > 0 ? overrideWidth : Math.max(150, calculatedWidth);
    const actionColumn: ColDef = {
      field: 'actions',
      headerName: 'Actions',
      width: finalWidth,
      minWidth: Math.min(150, finalWidth),
      maxWidth: Math.max(500, finalWidth),
      sortable: false,
      filter: false,
      resizable: true,
      pinned: 'right',
      autoHeight: false,
      suppressSizeToFit: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
        overflow: 'visible',
      },
      cellRenderer: CustomActionCellRendererComponent,
      cellRendererParams: {
        actions: actions,
        onActionClick: onActionClick,
      },
    };

    return [...columns, actionColumn];
  }

  private calculateActionColumnWidth(actions: ActionConfig[]): number {
    const actionCount = actions.length;
    const isIconOnly = actionCount >= 5;
    const isCompact = actionCount >= 3 && actionCount <= 4;

    let totalWidth = 8;

    for (const action of actions) {
      const labelLength = action.label?.length || 0;
      let buttonWidth: number;

      if (isIconOnly) {
        buttonWidth = 32;
      } else if (isCompact) {
        buttonWidth = Math.max(36, 14 + 3 + labelLength * 6 + 10);
      } else {
        buttonWidth = Math.max(44, 18 + 4 + labelLength * 7 + 12);
      }

      totalWidth += buttonWidth;
    }

    totalWidth += Math.max(0, (actionCount - 1) * 2) + 12;
    return Math.min(totalWidth, 450);
  }
}
